/**
 * Shopify Wishlist Script (Optimized for Minimog Theme)
 * Works on both desktop and mobile devices.
 * Saves wishlist to localStorage and syncs across all pages.
 */

class Wishlist {
  constructor() {
    this.storageKey = 'm-wishlist-products';
    this.pageTemplate = 'page.wishlist';
    this.addedClass = 'added-to-wishlist';
    this.hasItemClass = 'wishlist-has-item';
    this.isWishlistPage = MinimogSettings?.template === this.pageTemplate;

    this.selectors = {
      container: '.m-wishlist-page-content__wrapper',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count'
    };

    this.products = [...new Set(JSON.parse(localStorage.getItem(this.storageKey)) || [])];
    this.productNodes = {};

    this.init();
  }

  /** ---------- INIT ---------- */
  async init() {
    if (this.isWishlistPage) {
      await this.renderWishlistPage();
      this.addEventToRemoveButtons();
    }
    this.setWishlistButtonsState();
    this.addEventToWishlistButtons();
    this.updateWishlistCount();
  }

  /** ---------- STORAGE ---------- */
  saveToStorage() {
    this.products = [...new Set(this.products)];
    localStorage.setItem(this.storageKey, JSON.stringify(this.products));
  }

  addToWishlist(handle) {
    if (!handle || this.products.includes(handle)) return;
    this.products.push(handle);
    this.saveToStorage();
  }

  removeFromWishlist(handle) {
    this.products = this.products.filter(h => h !== handle);
    this.saveToStorage();
  }

  /** ---------- BUTTONS ---------- */
  setWishlistButtonsState() {
    document.querySelectorAll(this.selectors.wishlistButton).forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (this.products.includes(handle)) {
        this.toggleButtonState(btn, true);
        if (this.isWishlistPage) {
          btn.classList.replace(
            this.selectors.wishlistButton.slice(1),
            this.selectors.removeButton.slice(1)
          );
        }
      }
    });
  }

  updateWishlistCount() {
    const size = this.products.length;
    document.querySelectorAll(this.selectors.count).forEach(el => {
      el.textContent = size;
      el.classList.toggle('m:hidden', size < 1);
    });
    document.body.classList.toggle(this.hasItemClass, size > 0);
  }

  addEventToWishlistButtons() {
    document.addEventListener('click', e => {
      const btn = e.target.closest(this.selectors.wishlistButton);
      if (!btn) return;
      e.preventDefault();

      const handle = btn.dataset.productHandle;
      if (!handle) return;

      const isActive = !btn.classList.contains(this.addedClass);
      this.toggleButtonState(btn, isActive);
      this.updateWishlistCount();

      // Sync all same-product hearts
      document.querySelectorAll(this.selectors.wishlistButton).forEach(b => {
        if (b !== btn && b.dataset.productHandle === handle) {
          this.toggleButtonState(b, isActive);
        }
      });
    });
  }

  toggleButtonState(btn, active) {
    const handle = btn.dataset.productHandle;
    const textEl = btn.querySelector(this.selectors.wishlistText);

    if (active) {
      this.addToWishlist(handle);
      btn.classList.add(this.addedClass);
    } else {
      this.removeFromWishlist(handle);
      btn.classList.remove(this.addedClass);
    }

    if (textEl) {
      const temp = textEl.dataset.revertText;
      textEl.dataset.revertText = textEl.textContent;
      textEl.textContent = temp;
    }
  }

  /** ---------- REMOVE ---------- */
  addEventToRemoveButtons() {
    document.addEventListener('click', e => {
      const btn = e.target.closest(this.selectors.removeButton);
      if (!btn) return;
      e.preventDefault();

      const wrapper = btn.closest(this.selectors.wrapper);
      wrapper?.remove();

      const handle = btn.dataset.productHandle;
      if (handle) {
        this.removeFromWishlist(handle);
        this.updateWishlistCount();
      }

      if (!this.products.length) this.showNoProductsMessage();
    });
  }

  /** ---------- PAGE RENDER ---------- */
  async renderWishlistPage() {
    const container = document.querySelector(this.selectors.container);
    if (!container) return;

    if (!this.products.length) return this.showNoProductsMessage();

    let hasProducts = false;
    for (const handle of this.products) {
      const url = formatUrl('products', handle, 'view=grid-card-item');
      const prodHTML = await fetchCache(url);
      const item = document.createElement('div');
      item.className = 'm:hidden m:column m-wishlist-card';
      item.innerHTML = prodHTML;

      if (item.querySelector(this.selectors.productCard)) {
        hasProducts = true;
        item.appendChild(this.createRemoveButton(handle));
        this.productNodes[handle] = item;
        container.appendChild(item);
        item.classList.remove('m:hidden');
      }
    }

    if (!hasProducts) this.showNoProductsMessage();
    else this.setWishlistButtonsState();

    container.classList.add('is-visible');
  }

  createRemoveButton(handle) {
    const html = `
      <svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>`;
    const btn = document.createElement('div');
    btn.className = 'm-tooltip m-wishlist-remove-button m:block md:m:hidden';
    btn.dataset.productHandle = handle;
    btn.innerHTML = html;
    return btn;
  }

  showNoProductsMessage() {
    const container = document.querySelector(this.selectors.container);
    const noProducts = document.querySelector(this.selectors.noProducts);
    container?.classList.add('m:hidden');
    noProducts?.classList.remove('m:hidden');
  }
}

// ✅ Initialize wishlist globally
document.addEventListener('DOMContentLoaded', () => {
  MinimogTheme.Wishlist = new Wishlist();
});
